import React, { useState } from 'react';
import { uploadCSV, previewCSV } from '../../services/api';

function CSVUpload({ onBack }) {
  const [file, setFile] = useState(null);
  const [sourceName, setSourceName] = useState('manual');
  const [skipToday, setSkipToday] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = async (selectedFile) => {
    setFile(selectedFile);
    setResult(null);
    setError(null);
    setPreview(null);

    // Auto-preview
    setPreviewing(true);
    try {
      const previewData = await previewCSV(selectedFile);
      setPreview(previewData);

      // Auto-detect source based on format/columns
      if (previewData.format_detected === 'biometrics' || previewData.format_detected === 'cronometer_energy') {
        setSourceName('cronometer');
      } else if (previewData.categories_found.nutrition && !previewData.categories_found.activity) {
        setSourceName('cronometer');
      } else if (previewData.categories_found.activity && !previewData.categories_found.nutrition) {
        setSourceName('garmin');
      }
    } catch (err) {
      setError('Vorschau fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Bitte wähle eine Datei aus');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const response = await uploadCSV(file, sourceName, 1, skipToday);
      setResult(response);
      setFile(null);
      setPreview(null);
    } catch (err) {
      setError('Upload fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="csv-upload">
      <div className="header">
        <button onClick={onBack} className="back-button">← Zurück</button>
        <h2>CSV Import</h2>
      </div>

      {!preview && !result && (
        <div className="upload-info">
          <h3>Unterstützte Formate</h3>
          <p>
            Der Import erkennt automatisch Spalten aus verschiedenen Quellen:
          </p>
          <ul>
            <li><strong>Cronometer:</strong> Energy (kcal), Protein (g), Fat (g), Carbs (g), Sodium (mg), etc.</li>
            <li><strong>Garmin:</strong> Aktiv-Kalorien, Kalorien in Ruhe, Gesamt, Schritte, etc.</li>
            <li><strong>Generisch:</strong> calories_consumed, weight_kg, steps, etc.</li>
          </ul>
          <p className="hint">
            Die Spaltenreihenfolge ist egal - der Import matcht nach Spaltennamen.
          </p>
        </div>
      )}

      {!result && (
        <div className="upload-section">
          <div
            className={`dropzone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {previewing ? (
              <p>Analysiere Datei...</p>
            ) : file ? (
              <p>
                <strong>{file.name}</strong>
                <br />
                <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
              </p>
            ) : (
              <p>CSV-Datei hierher ziehen oder klicken</p>
            )}
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload" className="file-label">
              {file ? 'Andere Datei wählen' : 'Datei auswählen'}
            </label>
          </div>
        </div>
      )}

      {/* Preview Section */}
      {preview && !result && (
        <div className="preview-section">
          <h3>Vorschau: {preview.filename}</h3>

          {/* Summary */}
          <div className="preview-summary">
            <div className="summary-item">
              <span className="label">Zeilen</span>
              <span className="value">{preview.total_rows}</span>
            </div>
            <div className="summary-item">
              <span className="label">Zeitraum</span>
              <span className="value">
                {preview.date_range.start} bis {preview.date_range.end}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Tage</span>
              <span className="value">{preview.date_range.days_count}</span>
            </div>
            <div className="summary-item">
              <span className="label">Spalten erkannt</span>
              <span className="value">
                {preview.columns.recognized} / {preview.columns.total}
              </span>
            </div>
          </div>

          {/* Warnings */}
          {preview.warnings.length > 0 && (
            <div className="preview-warnings">
              {preview.warnings.map((warning, idx) => (
                <div key={idx} className="warning">⚠️ {warning}</div>
              ))}
            </div>
          )}

          {/* Categories Found */}
          <div className="preview-categories">
            <h4>Erkannte Daten</h4>
            <div className="categories-grid">
              <div className={`category ${preview.categories_found.nutrition ? 'found' : 'not-found'}`}>
                <span className="icon">{preview.categories_found.nutrition ? '✓' : '✗'}</span>
                <span className="name">Ernährung</span>
                {preview.categories_found.nutrition && (
                  <span className="fields">{preview.categories_found.nutrition_fields.join(', ')}</span>
                )}
              </div>
              <div className={`category ${preview.categories_found.activity ? 'found' : 'not-found'}`}>
                <span className="icon">{preview.categories_found.activity ? '✓' : '✗'}</span>
                <span className="name">Aktivität</span>
                {preview.categories_found.activity && (
                  <span className="fields">{preview.categories_found.activity_fields.join(', ')}</span>
                )}
              </div>
              <div className={`category ${preview.categories_found.weight ? 'found' : 'not-found'}`}>
                <span className="icon">{preview.categories_found.weight ? '✓' : '✗'}</span>
                <span className="name">Gewicht</span>
              </div>
            </div>
          </div>

          {/* Column Mappings */}
          <div className="preview-columns">
            <h4>Spalten-Zuordnung ({preview.columns.recognized} erkannt)</h4>
            <table className="mappings-table">
              <thead>
                <tr>
                  <th>CSV-Spalte</th>
                  <th>→</th>
                  <th>Datenbank-Feld</th>
                  <th>Werte</th>
                  <th>Beispiele</th>
                </tr>
              </thead>
              <tbody>
                {preview.detected_mappings.map((mapping, idx) => (
                  <tr key={idx} className="recognized">
                    <td>{mapping.csv_column}</td>
                    <td>→</td>
                    <td>{mapping.maps_to_label}</td>
                    <td className="count">{mapping.values_count}</td>
                    <td className="samples">{(mapping.sample_values || []).slice(0, 2).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {preview.unrecognized_columns.length > 0 && (
              <>
                <h4 className="unrecognized-header">
                  Nicht erkannte Spalten ({preview.unrecognized_columns.length})
                </h4>
                <table className="mappings-table unrecognized">
                  <tbody>
                    {preview.unrecognized_columns.map((col, idx) => (
                      <tr key={idx}>
                        <td>{col.csv_column}</td>
                        <td>→</td>
                        <td className="ignored">wird ignoriert</td>
                        <td className="count">{col.values_count}</td>
                        <td className="samples">{(col.sample_values || []).slice(0, 2).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Import Options */}
          <div className="import-options">
            <h4>Import-Optionen</h4>
            <div className="options-grid">
              <label className="option">
                <span>Datenquelle:</span>
                <select value={sourceName} onChange={(e) => setSourceName(e.target.value)}>
                  <option value="manual">Manual</option>
                  <option value="garmin">Garmin</option>
                  <option value="cronometer">Cronometer</option>
                  <option value="health_connect">Health Connect</option>
                </select>
              </label>
              <label className="option checkbox">
                <input
                  type="checkbox"
                  checked={skipToday}
                  onChange={(e) => setSkipToday(e.target.checked)}
                />
                <span>Heutigen Tag überspringen (meist unvollständig)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="upload-actions">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="primary"
            >
              {uploading ? 'Importiere...' : `${preview.total_rows} Zeilen importieren`}
            </button>
            <button onClick={resetForm}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="result-section">
          <div className="message success">
            <h3>Import erfolgreich!</h3>
            <p>{result.message}</p>
            <div className="result-stats">
              <div className="stat">
                <span className="value">{result.records_imported}</span>
                <span className="label">Neu</span>
              </div>
              <div className="stat">
                <span className="value">{result.records_updated}</span>
                <span className="label">Aktualisiert</span>
              </div>
              <div className="stat">
                <span className="value">{result.records_skipped}</span>
                <span className="label">Übersprungen</span>
              </div>
            </div>
          </div>
          <div className="upload-actions">
            <button onClick={resetForm} className="primary">
              Weitere Datei importieren
            </button>
            <button onClick={onBack}>
              Zurück zum Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="message error">
          <p>{error}</p>
        </div>
      )}

      <style>{`
        .csv-upload {
          max-width: 900px;
          margin: 0 auto;
          padding: var(--space-4, 1rem);
        }

        .header {
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
          margin-bottom: var(--space-6, 1.5rem);
        }

        .back-button {
          background: var(--card-bg, white);
          border: 1px solid var(--border-color, #e5e7eb);
          padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
          cursor: pointer;
          border-radius: var(--radius-md, 8px);
          color: var(--text-primary, #1f2937);
          transition: all 0.2s;
        }

        .back-button:hover {
          background: var(--bg-color, #f9fafb);
          border-color: var(--border-hover, #d1d5db);
        }

        h2 {
          margin: 0;
          color: var(--text-primary, #1f2937);
          font-weight: 700;
        }

        .upload-info {
          background: var(--card-bg, white);
          padding: var(--space-6, 1.5rem);
          border-radius: var(--radius-lg, 12px);
          margin-bottom: var(--space-6, 1.5rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .upload-info h3 {
          margin-top: 0;
          color: var(--text-primary, #1f2937);
        }

        .upload-info ul {
          margin: var(--space-2, 0.5rem) 0;
          padding-left: var(--space-6, 1.5rem);
          color: var(--text-primary, #1f2937);
        }

        .upload-info li {
          margin-bottom: var(--space-2, 0.5rem);
        }

        .hint {
          color: var(--text-secondary, #6b7280);
          font-size: 0.9rem;
          margin-top: var(--space-4, 1rem);
          margin-bottom: 0;
        }

        .dropzone {
          border: 2px dashed var(--border-color, #e5e7eb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-8, 2rem);
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          background: var(--bg-color, #f9fafb);
          color: var(--text-primary, #1f2937);
        }

        .dropzone.active {
          border-color: var(--primary-500, #3b82f6);
          background: var(--primary-50, #eff6ff);
        }

        .dropzone.has-file {
          border-color: var(--success-500, #22c55e);
          background: var(--success-50, #f0fdf4);
        }

        .file-label {
          display: inline-block;
          margin-top: var(--space-4, 1rem);
          padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
          background: var(--primary-600, #2563eb);
          color: white;
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .file-label:hover {
          background: var(--primary-700, #1d4ed8);
        }

        .file-size {
          color: var(--text-secondary, #6b7280);
          font-size: 0.9rem;
        }

        /* Preview Section */
        .preview-section {
          background: var(--card-bg, white);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-6, 1.5rem);
          margin-top: var(--space-6, 1.5rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .preview-section h3 {
          margin-top: 0;
          padding-bottom: var(--space-2, 0.5rem);
          border-bottom: 2px solid var(--primary-500, #3b82f6);
          color: var(--text-primary, #1f2937);
        }

        .preview-summary {
          display: flex;
          gap: var(--space-8, 2rem);
          margin: var(--space-4, 1rem) 0;
          flex-wrap: wrap;
        }

        .summary-item {
          display: flex;
          flex-direction: column;
        }

        .summary-item .label {
          font-size: 0.75rem;
          color: var(--text-muted, #9ca3af);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
        }

        .summary-item .value {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
        }

        .preview-warnings {
          margin: var(--space-4, 1rem) 0;
        }

        .warning {
          background: var(--warning-50, #fffbeb);
          border: 1px solid var(--warning-500, #f59e0b);
          padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
          border-radius: var(--radius-md, 8px);
          color: var(--warning-700, #b45309);
        }

        .preview-categories h4,
        .preview-columns h4,
        .import-options h4 {
          margin: var(--space-6, 1.5rem) 0 var(--space-3, 0.75rem);
          font-size: 1rem;
          color: var(--text-primary, #1f2937);
          font-weight: 600;
        }

        .categories-grid {
          display: flex;
          gap: var(--space-4, 1rem);
          flex-wrap: wrap;
        }

        .category {
          display: flex;
          flex-direction: column;
          padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
          border-radius: var(--radius-md, 8px);
          min-width: 150px;
        }

        .category.found {
          background: var(--success-50, #f0fdf4);
          border: 1px solid var(--success-500, #22c55e);
        }

        .category.not-found {
          background: var(--bg-color, #f9fafb);
          border: 1px solid var(--border-color, #e5e7eb);
          color: var(--text-secondary, #6b7280);
        }

        .category .icon {
          font-size: 1.2rem;
        }

        .category .name {
          font-weight: 600;
          color: var(--text-primary, #1f2937);
        }

        .category.not-found .name {
          color: var(--text-secondary, #6b7280);
        }

        .category .fields {
          font-size: 0.8rem;
          color: var(--success-700, #15803d);
          margin-top: var(--space-1, 0.25rem);
        }

        .mappings-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          margin-top: var(--space-2, 0.5rem);
        }

        .mappings-table th,
        .mappings-table td {
          padding: var(--space-2, 0.5rem);
          text-align: left;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .mappings-table th {
          background: var(--bg-color, #f9fafb);
          font-weight: 600;
          color: var(--text-muted, #9ca3af);
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
        }

        .mappings-table td {
          color: var(--text-primary, #1f2937);
        }

        .mappings-table tr.recognized td {
          background: var(--success-50, #f0fdf4);
        }

        .mappings-table.unrecognized td {
          color: var(--text-secondary, #6b7280);
        }

        .mappings-table .count {
          text-align: center;
          color: var(--text-secondary, #6b7280);
        }

        .mappings-table .samples {
          font-size: 0.8rem;
          color: var(--text-secondary, #6b7280);
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mappings-table .ignored {
          color: var(--error-500, #ef4444);
          font-style: italic;
        }

        .unrecognized-header {
          color: var(--error-500, #ef4444) !important;
        }

        .import-options {
          background: var(--bg-color, #f9fafb);
          padding: var(--space-4, 1rem);
          border-radius: var(--radius-md, 8px);
          margin-top: var(--space-4, 1rem);
        }

        .options-grid {
          display: flex;
          gap: var(--space-8, 2rem);
          flex-wrap: wrap;
        }

        .option {
          display: flex;
          align-items: center;
          gap: var(--space-2, 0.5rem);
          color: var(--text-primary, #1f2937);
        }

        .option.checkbox {
          cursor: pointer;
        }

        .option select {
          padding: var(--space-1, 0.25rem) var(--space-2, 0.5rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-sm, 6px);
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
        }

        .upload-actions {
          display: flex;
          gap: var(--space-4, 1rem);
          margin-top: var(--space-6, 1.5rem);
        }

        .upload-actions button {
          padding: var(--space-3, 0.75rem) var(--space-6, 1.5rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          transition: all 0.2s;
        }

        .upload-actions button:hover {
          background: var(--bg-color, #f9fafb);
        }

        .upload-actions button.primary {
          background: var(--success-500, #22c55e);
          color: white;
          border-color: var(--success-500, #22c55e);
        }

        .upload-actions button.primary:hover:not(:disabled) {
          background: var(--success-600, #16a34a);
        }

        .upload-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Result Section */
        .result-section {
          margin-top: var(--space-6, 1.5rem);
        }

        .message {
          padding: var(--space-6, 1.5rem);
          border-radius: var(--radius-lg, 12px);
          margin-bottom: var(--space-4, 1rem);
        }

        .message.success {
          background: var(--success-50, #f0fdf4);
          border: 1px solid var(--success-500, #22c55e);
        }

        .message.success h3 {
          color: var(--success-700, #15803d);
        }

        .message.error {
          background: var(--error-50, #fef2f2);
          border: 1px solid var(--error-500, #ef4444);
          color: var(--error-700, #b91c1c);
        }

        .message h3 {
          margin-top: 0;
        }

        .message p {
          color: var(--text-primary, #1f2937);
        }

        .result-stats {
          display: flex;
          gap: var(--space-8, 2rem);
          margin-top: var(--space-4, 1rem);
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stat .value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--success-700, #15803d);
        }

        .stat .label {
          font-size: 0.85rem;
          color: var(--success-600, #16a34a);
        }

        @media (max-width: 768px) {
          .preview-summary {
            gap: var(--space-4, 1rem);
          }

          .options-grid {
            flex-direction: column;
            gap: var(--space-3, 0.75rem);
          }

          .upload-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default CSVUpload;
